import { CONTROLLER, METHOD, QUERY } from "common/config/endpoint.config";
import { ApiConfig } from "common/models";
import { ListParam } from "../models/document.model";
import {
  AddTagRequest,
  DeleteMultipleTagRequest,
  EditRewrittenRequirementRequest,
  RedoTaggingRequest,
  TagListRequest,
  TagsBySearchTermRequest
} from "../models/tags.model";
import { AddRuleRequest, FailedRulesRequest } from "../models/rules.model";
import {
  AddRewrittenRequirementRequest,
  DeleteRewrittenRequirementRequest,
  TriggerFinalizeDocumentRequest,
  TriggerRewriteRequest,
  TriggerTaggingRequest,
  TriggerTaggingWithoutRewriteRequest
} from "../models/rewritten-requirement.model";
import {
  AddRequirementRequest,
  DeleteMultipleRequirementsRequest,
  ReEvaluateReferencesRequest,
  ReEvaluateSimilarRequirementsRequest,
  ReferenceDataRequest,
  RevertRequirementRequest,
  RewriteRequirementRequest,
  SimilarRequirementsListRequest,
  UpdateRequirementNotesRequest,
  UpdateRequirementRequest,
  UpdateSimilarRequirementsFeedbackRequest,
  downloadReferenceDocRequest
} from "../models/requirements.model";
import { AddRequirementToHierarchyRequest } from "../models/add-requirement-to-hierarchy.model";
import { RemoveRequirementFromHierarchyRequest } from "../models/remove-requirement-from-hierarchy.model";
import {
  ReorderRewrittenRequirementItemDetails,
  ReorderRewrittenRequirementRequest
} from "../models/reorder-rewritten-requirement.model";
import {
  DocumentUploadRequest,
  DocumentUpdateRequest
} from "common/models/document.model";
import { buildEngApiUrl } from "common/utils/api.util";

const buildDocumentsApiUrl = (path: string): string => buildEngApiUrl(path);

export const documentsConfig = {
  getAllDocuments: (listParams: ListParam): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.DOCUMENTS}`),
      "POST",
      undefined,
      listParams
    );
  },

  getAdminDocuments: (listParams: any): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.ADMIN}/${CONTROLLER.DOCUMENTS}`),
      "POST",
      `${QUERY.HAS_DUPLICATE}=${listParams.params.showDuplicates}`,
      listParams.params
    );
  },

  updateDocument: (
    documentId: string,
    documentUpdateRequest: DocumentUpdateRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.ADMIN}/${CONTROLLER.DOCUMENT}/${documentId}/${METHOD.UPDATE_DOCUMENT}`
      ),
      "PUT",
      undefined,
      documentUpdateRequest
    );
  },

  getDoumentDetail: (documentId: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.DOCUMENTS}/${documentId}`),
      "GET",
      undefined,
      undefined
    );
  },

  uploadDocument: (documentUploadRequest: DocumentUploadRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.UPLOAD}`),
      "POST",
      `${QUERY.REFERENCE_TYPE}=${documentUploadRequest.referenceType}&${
        documentUploadRequest.isReferenceDocument
          ? `${QUERY.IS_REFERENCE_DOCUMENT}=true`
          : ""
      }&${QUERY.GP_NAME}=${documentUploadRequest.gpName}`,
      documentUploadRequest.file
    );
  },

  getDocumentRequirements: (
    documentId: string,
    listParams: ListParam
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentId}/${METHOD.REQUIREMENTS}/${METHOD.LIST}`
      ),
      "POST",
      undefined,
      listParams
    );
  },

  deleteDocument: (documentId: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.DOCUMENTS}/${documentId}`),
      "DELETE",
      undefined,
      undefined
    );
  },

  getDocumentTags: (documentId: string, reqIds: string[] = []): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentId}/${METHOD.TAGS}`
      ),
      "POST",
      undefined,
      reqIds
    );
  },

  getNonExistingDocuments: (documentId: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentId}/${METHOD.NON_EXISTENT_GP}`
      ),
      "GET",
      undefined,
      undefined
    );
  },

  downloadDocuments: (documentId: string, downloadType: string): ApiConfig => {
    if (downloadType === "document report") {
      return new ApiConfig(
        buildDocumentsApiUrl(
          `${CONTROLLER.DOCUMENTS}/${documentId}/${METHOD.DOWNLOAD}`
        ),
        "GET",
        undefined,
        undefined
      );
    } else {
      return new ApiConfig(
        buildDocumentsApiUrl(
          `${CONTROLLER.REPORT}/${documentId}/${METHOD.DOWNLOAD_SIMILAR_REQUIREMENT_DETAILS}`
        ),
        "GET",
        undefined,
        undefined
      );
    }
  },

  deleteDocumentTag: (deleteTagRequest: any): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${deleteTagRequest.requirementId}/${METHOD.TAGS}`
      ),
      "DELETE",
      undefined,
      deleteTagRequest
    );
  },
  deleteMultipleDocumentTag: (
    deleteMultipleTagRequest: DeleteMultipleTagRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.DELETE_MULTIPLE_TAG}`
      ),
      "DELETE",
      undefined,
      deleteMultipleTagRequest
    );
  },

  getTagsList: (tagListRequestObj: TagListRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${tagListRequestObj.rewrittenRequirementGuid}/CFIHOSObjects?serachText=${tagListRequestObj.searchValue}&cfihosMasterId=${tagListRequestObj.cfihosObjectId}`
      ),
      "GET",
      undefined,
      undefined
    );
  },

  getTagsBySearchTerm: (request: TagsBySearchTermRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.SEARCH_TAGS}`
      ),
      "GET",
      `${QUERY.CFIHOS_MASTER_ID}=${request.cfihosMasterId}&${QUERY.SEARCH_TEXT}=${request.serachText}`
    );
  },

  addTags: (addTagRequestObj: AddTagRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(`${CONTROLLER.DOCUMENTS}/${METHOD.TAGS}`),
      "POST",
      undefined,
      addTagRequestObj
    );
  },

  editRewrittenRequirement: (
    editRewrittenRequirementRequestObj: EditRewrittenRequirementRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${editRewrittenRequirementRequestObj.documentGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${editRewrittenRequirementRequestObj.rewrittenRequirementGuid}`
      ),
      "PUT",
      undefined,
      {
        rewrittenRequirementText: (editRewrittenRequirementRequestObj as any)
          .requirementText,
        status: "New"
      }
    );
  },

  getClassList: (): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.MASTERDATA}/${METHOD.CFIHOSMASTEROBJECTS}`
      ),
      "GET",
      undefined,
      undefined
    );
  },

  getFailedRules: (failedRulesRequest: FailedRulesRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${failedRulesRequest.documentGuid}/${METHOD.FAILEDRULES}`
      ),
      "POST",
      undefined,
      failedRulesRequest
    );
  },

  addRewrittenRequirement: (
    addRewrittenRequirement: AddRewrittenRequirementRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${addRewrittenRequirement.documentGuid}/${METHOD.REQUIREMENTS}/${addRewrittenRequirement.requirementGuid}/${METHOD.REWRITTEN_REQUIREMENTS}`
      ),
      "POST",
      undefined,
      [
        {
          requirementText: addRewrittenRequirement.requirementText
        }
      ]
    );
  },

  deleteRewrittenRequirement: (
    deleteRewrittenRequirementRequest: DeleteRewrittenRequirementRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${deleteRewrittenRequirementRequest.requirementGuid}`
      ),
      "DELETE",
      undefined,
      deleteRewrittenRequirementRequest
    );
  },

  deleteOriginalRequirement: (
    deleteOriginalRequirementRequest: DeleteRewrittenRequirementRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${deleteOriginalRequirementRequest.requirementGuid}`
      ),
      "DELETE"
    );
  },

  triggerReWrite: (triggerRewriteRequest: TriggerRewriteRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${triggerRewriteRequest.documentGuid}/${METHOD.REQUIREMENTS}/${METHOD.REWRITE}`
      ),
      "POST",
      "",
      { requirementGuids: triggerRewriteRequest.requirementGuids }
    );
  },

  triggerTagging: ({
    documentGuid,
    requirementGuids,
    tagAttributeGuids
  }: TriggerTaggingRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.TAG}`
      ),
      "POST",
      "",
      {
        requirementGuids: requirementGuids,
        tagAttributeGuids: tagAttributeGuids
      }
    );
  },

  triggerTaggingWithoutRewrite: ({
    documentGuid,
    requirementGuids,
    tagAttributeGuids
  }: TriggerTaggingWithoutRewriteRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/${METHOD.REQUIREMENTS}/${METHOD.TAG}`
      ),
      "POST",
      "",
      {
        requirementGuids: requirementGuids,
        tagAttributeGuids: tagAttributeGuids
      }
    );
  },

  deleteFailedRule: (deleteFailedRuleRequest: any): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.FAILEDRULES}/${deleteFailedRuleRequest.failedRuleGuid}`
      ),
      "DELETE",
      undefined,
      { reason: deleteFailedRuleRequest.reason }
    );
  },
  addRule: (addRuleRequest: AddRuleRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${addRuleRequest.documentGuid}/${METHOD.REQUIREMENTS}/${addRuleRequest.requirementGuid}/${METHOD.FAILEDRULES}`
      ),
      "POST",
      undefined,
      {
        rules: [
          {
            id: addRuleRequest.id,
            reason: addRuleRequest.reason,
            isAI: addRuleRequest.isAI
          }
        ]
      }
    );
  },

  getRewrittenRequirementRecommendedTags: (
    documentGuidId: string,
    rewrittenRequirementGuids: string[]
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuidId}/${METHOD.RECOMMENDED_TAGS}`
      ),
      "POST",
      undefined,
      rewrittenRequirementGuids
    );
  },
  addRequirement: (addRequirement: AddRequirementRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${addRequirement.documentGuid}/${METHOD.REQUIREMENTS}`
      ),
      "POST",
      undefined,
      {
        requirementText: addRequirement.requirementText
      }
    );
  },
  updateRequirement: (
    updateRequirement: UpdateRequirementRequest
  ): ApiConfig => {
    const isDocTypeAvailable =
      updateRequirement.tags === undefined &&
      updateRequirement.processUnit === undefined &&
      updateRequirement.title === undefined;
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${updateRequirement.requirementGuid}`
      ),
      "PUT",
      undefined,
      isDocTypeAvailable
        ? {
            requirementText: updateRequirement.requirementText
          }
        : {
            requirementText: updateRequirement.requirementText,
            documentTypeInfo: {
              tags: updateRequirement.tags,
              processUnit: updateRequirement.processUnit,
              title: updateRequirement.title
            }
          }
    );
  },
  rewriteRequirement: (
    rewriteRequirement: RewriteRequirementRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${rewriteRequirement.documentGuid}/${METHOD.REQUIREMENTS}/${METHOD.REWRITE}`
      ),
      "POST",
      undefined,
      {
        requirementGuids: rewriteRequirement.requirementGuids
      }
    );
  },
  redoTagging: (redoTagging: RedoTaggingRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${redoTagging.documentGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.TAG}`
      ),
      "POST",
      undefined,
      {
        rewrittenRequirementGuids: redoTagging.rewrittenRequirementGuids,
        tagAttributeGuids: redoTagging.tagAttributeGuids
      }
    );
  },
  triggerFinalizeDocument: ({
    documentGuid
  }: TriggerFinalizeDocumentRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/${METHOD.FINALIZE}`
      ),
      "POST",
      ""
    );
  },

  getReferenceData: (request: ReferenceDataRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REFERENCES}`
      ),
      "GET",
      `${QUERY.REFERENCE_ID}=${request.referenceId}`
    );
  },

  downloadReferenceDoc: (reuest: downloadReferenceDocRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${reuest.documentGuid}/${METHOD.ORIGINAL}/${METHOD.DOWNLOAD}`
      ),
      "GET"
    );
  },

  reEvaluateReferences: (request: ReEvaluateReferencesRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REFERENCES}/${METHOD.RUN}`
      ),
      "POST"
    );
  },

  revertRequirement: (request: RevertRequirementRequest): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REVERT}`
      ),
      "POST"
    );
  },

  getSimilarRequirementsList: (
    request: SimilarRequirementsListRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${request.documentGuid}/${METHOD.SIMILAR_REQUIREMENTS}/${METHOD.LIST}`
      ),
      "POST",
      undefined,
      {
        page: request?.page,
        size: request?.size,
        search: request?.search,
        requirementGuid: request?.requirementGuid,
        shouldIncludeOnlySameDocument: request?.shouldIncludeOnlySameDocument
      }
    );
  },

  updateSimilarRequirementsFeedback: (
    request: UpdateSimilarRequirementsFeedbackRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${request.documentGuid}/${METHOD.SIMILAR_REQUIREMENTS}`
      ),
      "PUT",
      undefined,
      {
        guidCode: request?.guidCode,
        isAIFlagged: request?.isAIFlagged,
        feedback: request?.feedback
      }
    );
  },

  deleteSimilarRequirement: (request: any): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${request.documentGuid}/${METHOD.SIMILAR_REQUIREMENTS}`
      ),
      "DELETE",
      undefined,
      {
        documentGuid: request.documentGuid,
        requirementGuid: request.requirementGuid,
        similarRequirementGuid: request.similarRequirementGuid,
        humanFeedback: request.humanFeedback
      }
    );
  },

  exportDocument: (exportDocumentGuid: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${exportDocumentGuid}/${METHOD.EXPORT}`
      ),
      "POST",
      undefined,
      undefined
    );
  },

  similaritySearch: (documentGuid: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/${METHOD.REQUIREMENTS}/${METHOD.SIMILARITY_SEARCH}`
      ),
      "POST",
      undefined,
      undefined
    );
  },

  deleteMultipleOriginalAndRewrittenReq: (
    deleteOriginalRequirementRequest: DeleteMultipleRequirementsRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.DELETE_REQUIREMENT_AND_REWRITTEN}`
      ),
      "DELETE",
      undefined,
      deleteOriginalRequirementRequest
    );
  },

  getRecommendedTagsForRewrittenRequirements: (
    documentGuidId: string,
    rewrittenRequirementGuids: string[]
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REWRITTEN_REQUIREMENTS}/${documentGuidId}/${METHOD.REQUIREMENT_RECOMMENDED_TAGS}`
      ),
      "POST",
      undefined,
      rewrittenRequirementGuids
    );
  },

  addRequirementToHierarchy: (
    request: AddRequirementToHierarchyRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.HIERARCHY}`
      ),
      "POST",
      undefined,
      {
        parentRewrittenRequirementGuid: request.parentRewrittenRequirementGuid,
        childRewrittenRequirementGuid: request.childRewrittenRequirementGuid
      }
    );
  },

  removeRequirementFromHierarchy: (
    request: RemoveRequirementFromHierarchyRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.HIERARCHY}/${METHOD.RESET}`
      ),
      "POST",
      undefined,
      {
        rewrittenRequirementGuid: request.rewrittenRequirementGuid
      }
    );
  },

  reorderRewrittenRequirement: (
    request: ReorderRewrittenRequirementRequest
  ): ApiConfig => {
    let payload: ReorderRewrittenRequirementItemDetails = {
      sourceRewrittenRequirementGuid: request.sourceRewrittenRequirementGuid
    };

    // if targetRewrittenRequirementGuid is present, then add it to payload
    if (request.targetRewrittenRequirementGuid) {
      payload = {
        ...payload,
        targetRewrittenRequirementGuid: request.targetRewrittenRequirementGuid
      };
    }

    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${METHOD.REQUIREMENTS}/${request.requirementGuid}/${METHOD.REWRITTEN_REQUIREMENTS}/${METHOD.REORDER}`
      ),
      "POST",
      undefined,
      payload
    );
  },

  reEvaluateSimilarRequirements: (
    request: ReEvaluateSimilarRequirementsRequest
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${request.documentGuid}/${METHOD.RE_EVALUATE_SIMILAR_REQUIREMENTS}`
      ),
      "POST"
    );
  },
  addNotes: (addNotes: UpdateRequirementNotesRequest): ApiConfig => {
    let method;
    if (addNotes.isRewrite) {
      method = METHOD.REWRITTEN_REQUIREMENTS;
    } else {
      method = METHOD.REQUIREMENTS;
    }
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${method}/${addNotes.requirementGuId}/${METHOD.NOTES}`
      ),
      "POST",
      undefined,
      {
        notes: addNotes.notes
      }
    );
  },
  updateNotes: (updateNotes: UpdateRequirementNotesRequest): ApiConfig => {
    let method;
    if (updateNotes.isRewrite) {
      method = METHOD.REWRITTEN_REQUIREMENTS;
    } else {
      method = METHOD.REQUIREMENTS;
    }
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${method}/${updateNotes.requirementGuId}/${METHOD.NOTES}`
      ),
      "PUT",
      undefined,
      {
        notes: updateNotes.notes
      }
    );
  },
  getNotes: (requirementGuid: string, isRewrite: boolean): ApiConfig => {
    let method;
    if (isRewrite) {
      method = METHOD.REWRITTEN_REQUIREMENTS;
    } else {
      method = METHOD.REQUIREMENTS;
    }
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${method}/${requirementGuid}/${METHOD.NOTES}`
      ),
      "GET",
      undefined,
      undefined
    );
  },
  deleteNotes: (requirementGuid: string, isRewrite: boolean): ApiConfig => {
    let method;
    if (isRewrite) {
      method = METHOD.REWRITTEN_REQUIREMENTS;
    } else {
      method = METHOD.REQUIREMENTS;
    }
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${method}/${requirementGuid}/${METHOD.NOTES}`
      ),
      "DELETE",
      undefined,
      undefined
    );
  },

  getMappedDocuments: (documentGuid: string): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/MappedDocuments`
      ),
      "GET",
      undefined,
      undefined
    );
  },

  deleteMappedDocument: (
    documentGuid: string,
    mappedDocumentGuid: string
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/MappedDocuments/${mappedDocumentGuid}`
      ),
      "DELETE",
      undefined,
      undefined
    );
  },

  addMappedDocument: (
    documentGuid: string,
    mappedDocumentGuid: string
  ): ApiConfig => {
    return new ApiConfig(
      buildDocumentsApiUrl(
        `${CONTROLLER.DOCUMENTS}/${documentGuid}/MappedDocuments/${mappedDocumentGuid}`
      ),
      "POST",
      undefined,
      undefined
    );
  }
};
